import { useId } from 'react'
import { Bot } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ManagementCopy } from '@/frontend/features/Fde/managementTypes'
import type { ProjectTaskAssignee } from '@/frontend/features/Fde/types'

interface Props {
  agents: ProjectTaskAssignee[]
  values: string[]
  label: string
  hint: string
  emptyLabel: string
  copy: ManagementCopy
  disabled?: boolean
  onChange: (values: string[]) => void
}

export default function AgentWakeSelector({
  agents,
  values,
  label,
  hint,
  emptyLabel,
  copy,
  disabled = false,
  onChange,
}: Props) {
  const id = useId()
  const selectedCount = agents.filter((agent) => values.includes(agent.value)).length
  const summary =
    selectedCount === 0
      ? copy.agent_wake_none
      : selectedCount === 1
        ? copy.agent_wake_one
        : copy.agent_wake_many.replace('%{count}', String(selectedCount))
  return (
    <fieldset
      disabled={disabled}
      aria-describedby={`${id}-hint ${id}-status`}
      className="rounded-lg border border-border/70 px-3 pb-3 pt-2 disabled:opacity-60"
    >
      <legend className="px-1 text-xs font-medium text-foreground">{label}</legend>
      <p id={`${id}-hint`} className="text-[11px] leading-4 text-muted-foreground">
        {agents.length === 0 ? emptyLabel : hint}
      </p>
      {agents.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {agents.map((agent) => {
            const selected = values.includes(agent.value)
            return (
              <label
                key={agent.value}
                className={cn(
                  'inline-flex min-h-9 max-w-full items-center gap-2.5 rounded-md border px-2.5 text-xs transition-colors',
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50',
                  selected
                    ? 'border-primary/35 bg-primary/5 text-foreground'
                    : 'border-border/70 bg-background text-foreground',
                )}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  aria-label={copy.agent_wake_agent_label.replace('%{name}', agent.name)}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...values, agent.value]
                        : values.filter((value) => value !== agent.value),
                    )
                  }
                  className="size-4 shrink-0 cursor-inherit rounded border-input accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                />
                {agent.avatar_url ? (
                  <img src={agent.avatar_url} alt="" className="size-5 rounded-full object-cover" />
                ) : (
                  <Bot className="size-4 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate">{agent.name}</span>
              </label>
            )
          })}
        </div>
      )}
      {agents.length > 0 && (
        <p
          id={`${id}-status`}
          role="status"
          className={cn(
            'mt-2 text-[11px] leading-4',
            selectedCount > 0 ? 'font-medium text-primary' : 'text-muted-foreground',
          )}
        >
          {summary}
        </p>
      )}
    </fieldset>
  )
}
